import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Report,
  GroupAdd,
  CalendarMonth,
  Payments,
  Notifications,
  AccountCircle,
  Logout,
  QrCodeScanner,
  QrCode2,
  EventAvailable,
  Storefront,
  AccountBalance,
  Home,
  ChevronRight,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import PushNotificationManager from './PushNotificationManager';
import myhomeLogo from '../assests/myhome-logo.png';

const drawerWidth = 240;
const mobileDrawerWidth = 'min(82vw, 320px)';

const menuIconStyles = {
  '/': { color: '#1976d2', background: '#e3f2fd' },
  '/users': { color: '#5e35b1', background: '#ede7f6' },
  '/buildings': { color: '#00897b', background: '#e0f2f1' },
  '/notifications': { color: '#d32f2f', background: '#ffebee' },
  '/payout-report-upload': { color: '#ef6c00', background: '#fff3e0' },
  '/visitors': { color: '#7b1fa2', background: '#f3e5f5' },
  '/facility-booking': { color: '#00897b', background: '#e0f2f1' },
  '/expenses': { color: '#ef6c00', background: '#fff3e0' },
  '/qr-generator': { color: '#3949ab', background: '#e8eaf6' },
  '/mass-notifications': { color: '#d32f2f', background: '#ffebee' },
  '/employee-details': { color: '#0288d1', background: '#e1f5fe' },
  '/staff-attendance': { color: '#2e7d32', background: '#e8f5e9' },
  '/community-marketplace': { color: '#c77800', background: '#fff8e1' },
  '/vehicle-security': { color: '#455a64', background: '#eceff1' },
  '/assigned-complaints': { color: '#c62828', background: '#ffebee' },
  '/announcements': { color: '#d32f2f', background: '#ffebee' },
};

const getMenuIconStyle = (path) => menuIconStyles[path] || {
  color: '#546e7a',
  background: '#eceff1',
};

const menuDescriptions = {
  '/users': 'Manage users and access',
  '/buildings': 'Manage buildings and properties',
  '/notifications': 'Review system notifications',
  '/payout-report-upload': 'Upload and review payout reports',
  '/visitors': 'Manage visitors and approvals',
  '/facility-booking': 'Book and manage facilities',
  '/expenses': 'Track building expenses',
  '/qr-generator': 'Create entry QR codes',
  '/mass-notifications': 'Send community announcements',
  '/employee-details': 'Manage employee information',
  '/staff-attendance': 'Review staff attendance',
  '/community-marketplace': 'Manage community listings',
  '/vehicle-security': 'Verify vehicles and entry',
  '/assigned-complaints': 'Review assigned complaints',
};

const Layout = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Menu items with translation keys
  const adminMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.users'), icon: <People />, path: '/users' },
    { text: t('layout.navigation.buildings'), icon: <Business />, path: '/buildings' },
    { text: t('layout.navigation.notifications'), icon: <Notifications />, path: '/notifications' },
    { text: 'Payout Reports', icon: <AccountBalance />, path: '/payout-report-upload' },
  ];

  const buildingMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.visitors'), icon: <GroupAdd />, path: '/visitors' },
    { text: t('layout.navigation.facilitiesBooking'), icon: <CalendarMonth />, path: '/facility-booking' },
    { text: t('layout.navigation.expenses'), icon: <Payments />, path: '/expenses' },
    { text: t('layout.navigation.qrGenerator'), icon: <QrCode2 />, path: '/qr-generator' },
    { text: t('layout.navigation.massNotifications'), icon: <Notifications />, path: '/mass-notifications' },
    { text: t('layout.navigation.EmployeeDetails'), icon: <People />, path: '/employee-details' },
    { text: t('layout.navigation.attendance', { defaultValue: 'Staff Attendance' }), icon: <EventAvailable />, path: '/staff-attendance' },
    { text: 'Community Marketplace', icon: <Storefront />, path: '/community-marketplace' }
  ];

  const maintenanceStaffMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.visitors'), icon: <GroupAdd />, path: '/visitors' },
    { text: t('layout.navigation.vehicleSecurity'), icon: <QrCodeScanner />, path: '/vehicle-security' },
    { text: t('layout.navigation.complaints', { defaultValue: 'Complaints' }), icon: <Report />, path: '/assigned-complaints' },
    { text: t('layout.navigation.attendance', { defaultValue: 'Attendance' }), icon: <EventAvailable />, path: '/staff-attendance' },
  ];

  const ownerTenantMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: 'Announcements', icon: <Notifications />, path: '/announcements' },
    { text: t('layout.navigation.visitors'), icon: <GroupAdd />, path: '/visitors' },
    { text: t('layout.navigation.facilitiesBooking'), icon: <CalendarMonth />, path: '/facility-booking' },
    { text: t('layout.navigation.EmployeeDetails'), icon: <People />, path: '/employee-details' },
    { text: 'Community Marketplace', icon: <Storefront />, path: '/community-marketplace' },
  ];

  // Determine which menu items to show based on user groups
  const groups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check user roles - also check the direct role property
  const userRole = user?.role;
  const isAdminOrStaff = groupNames.some(g => ['admin'].includes(g)) || userRole === 'super_admin';
  const isOwnerOrTenant = groupNames.some(g => ['owner', 'tenant'].includes(g)) || userRole === 'resident';
  const isBuildingAdmin = groupNames.some(g => ['building_admin'].includes(g)) || userRole === 'building_admin';
  const isMaintenanceStaff = groupNames.some(g => ['maintenance_staff'].includes(g)) || userRole === 'maintenance_staff';
  const showDashboardReturn = (isOwnerOrTenant || isMaintenanceStaff) && location.pathname !== '/';
  
  const menuItems = isAdminOrStaff ? adminMenuItems : 
                   isOwnerOrTenant ? ownerTenantMenuItems :
                   isBuildingAdmin ? buildingMenuItems : 
                   isMaintenanceStaff ? maintenanceStaffMenuItems : 
                   buildingMenuItems;

  const dashboardActions = menuItems.filter(item => item.path !== '/');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile drawer when navigating on mobile
  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false); // Always close drawer after navigation
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleMobileNavigation = (_event, value) => {
    navigate(value);
  };

  // Close mobile drawer when location changes (for browser navigation)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close drawer when resizing from mobile to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 900) { // md breakpoint; landscape phones stay compact
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const drawer = (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 72, sm: 64 },
          px: { xs: 2, sm: 2 },
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '6px 0', width: '100%' }}>
          <img
            src={myhomeLogo}
            alt={t('layout.logo.alt')}
            style={{
              height: '50px',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              margin: 'auto',
            }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ py: { xs: 1, sm: 0 } }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: { xs: 52, sm: 48 },
                px: { xs: 2, sm: 2 },
                py: { xs: 1, sm: 0.75 },
                mx: 1,
                my: 0.25,
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: getMenuIconStyle(item.path).background,
                  color: getMenuIconStyle(item.path).color,
                },
                '&.Mui-selected:hover': {
                  bgcolor: getMenuIconStyle(item.path).background,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  width: 36,
                  height: 36,
                  mr: 1.5,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 2,
                  color: location.pathname === item.path ? '#fff' : getMenuIconStyle(item.path).color,
                  bgcolor: location.pathname === item.path
                    ? getMenuIconStyle(item.path).color
                    : getMenuIconStyle(item.path).background,
                  transition: 'color 160ms ease, background-color 160ms ease, transform 160ms ease',
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: 23, sm: 22 },
                  },
                  '.MuiListItemButton-root:hover &': {
                    color: getMenuIconStyle(item.path).color,
                    bgcolor: getMenuIconStyle(item.path).background,
                    transform: 'translateY(-1px)',
                  },
                  '.MuiListItemButton-root.Mui-selected:hover &': {
                    color: '#fff',
                    bgcolor: getMenuIconStyle(item.path).color,
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: { xs: 16, sm: 16 },
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  lineHeight: 1.25,
                  noWrap: false,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      <PushNotificationManager />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          pt: { xs: 'env(safe-area-inset-top, 0px)', sm: 0 },
          background: { xs: 'linear-gradient(135deg, #0877dc 0%, #005fc7 100%)', sm: '#1976d2' },
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 64, sm: 64 },
            px: { xs: 1, sm: 3 },
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <IconButton
            color="inherit"
            aria-label={showDashboardReturn ? 'return to dashboard' : 'open drawer'}
            edge="start"
            onClick={showDashboardReturn ? () => navigate('/') : handleDrawerToggle}
            sx={{ mr: { xs: 0.5, sm: 2 }, display: { md: 'none' }, flexShrink: 0 }}
          >
            {showDashboardReturn ? <Home /> : <MenuIcon />}
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              minWidth: 0,
              fontSize: { xs: '1.05rem', sm: '1.25rem' },
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              {isOwnerOrTenant
                ? 'Owner/Tenant'
                : isBuildingAdmin
                  ? 'Building Admin'
                  : isMaintenanceStaff
                    ? 'Maintenance Staff'
                    : (menuItems.find(item => item.path === location.pathname)?.text || t('layout.navigation.dashboard'))}
            </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {isOwnerOrTenant
                ? t('layout.dashboards.ownerTenant')
                : isBuildingAdmin
                  ? t('layout.dashboards.buildingAdmin')
                  : isMaintenanceStaff
                    ? t('layout.dashboards.maintenanceStaff')
                    : (menuItems.find(item => item.path === location.pathname)?.text || t('layout.navigation.dashboard'))}
            </Box>
          </Typography>
          
          {/* Notification Bell - Show for all logged-in users */}
          <NotificationBell />
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{ flexShrink: 0, p: { xs: 0.75, sm: 1 } }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.firstName ? user.firstName.charAt(0).toUpperCase() : <AccountCircle />}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: mobileDrawerWidth,
              maxWidth: 'calc(100vw - 56px)',
              // Add smooth transition for better UX
              transition: 'transform 0.225s cubic-bezier(0, 0, 0.2, 1)',
              pt: 'env(safe-area-inset-top, 0px)',
              pb: 'env(safe-area-inset-bottom, 0px)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          // Ensure proper spacing and responsive behavior
          mt: { xs: 'calc(64px + env(safe-area-inset-top, 0px))', md: 0 }, // Account for compact AppBar height
          boxSizing: 'border-box',
          overflowX: 'hidden',
          pb: { xs: isOwnerOrTenant ? 'calc(88px + env(safe-area-inset-bottom, 0px))' : 2, md: 3 },
        }}
        className="main-content-container"
      >
        <Toolbar sx={{ display: { xs: 'none', md: 'block' } }} /> {/* Desktop toolbar spacer */}
        {location.pathname === '/' && !isOwnerOrTenant && dashboardActions.length > 0 && (
          <Box
            component="section"
            aria-label="Dashboard actions"
            sx={{
              display: { xs: 'grid', md: 'none' },
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1.25,
              mb: 2.5,
            }}
          >
            {dashboardActions.map(item => {
              const iconStyle = getMenuIconStyle(item.path);
              return (
                <Paper
                  key={item.path}
                  component="button"
                  type="button"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    position: 'relative',
                    minHeight: 142,
                    p: 1.5,
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    borderRadius: 3,
                    bgcolor: '#fff',
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(15,23,42,.07)',
                    transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': { transform: 'scale(.98)', boxShadow: '0 2px 8px rgba(15,23,42,.08)' },
                    '&:focus-visible': { outline: `3px solid ${iconStyle.background}`, borderColor: iconStyle.color },
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
                    <Box sx={{ width: 52, height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2.75, color: iconStyle.color, background: `linear-gradient(145deg, ${iconStyle.background}, #fff)`, border: `1px solid ${iconStyle.background}`, boxShadow: `0 8px 18px ${iconStyle.background}`, '& .MuiSvgIcon-root': { fontSize: 31 } }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0, pr: 4 }}>
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.15, color: '#111827' }}>{item.text}</Typography>
                      <Typography sx={{ mt: .55, color: '#667085', fontSize: '.76rem', lineHeight: 1.35 }}>
                        {menuDescriptions[item.path] || 'Open this dashboard option'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', color: iconStyle.color, bgcolor: iconStyle.background }}>
                    <ChevronRight />
                  </Box>
                  <Box sx={{ position: 'absolute', width: 115, height: 58, right: -30, bottom: -35, borderRadius: '50%', bgcolor: iconStyle.background, opacity: .8 }} />
                </Paper>
              );
            })}
          </Box>
        )}
        <Outlet />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          {t('layout.profile.profile')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('layout.profile.logout')}
        </MenuItem>
      </Menu>
      {isOwnerOrTenant && (
        <Paper
          elevation={10}
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme => theme.zIndex.drawer + 2,
            pb: 'env(safe-area-inset-bottom, 0px)',
            borderTop: '1px solid #e5e7eb',
            bgcolor: '#fff',
          }}
        >
          <BottomNavigation
            showLabels
            value={location.pathname === '/visitors' ? '/visitors' : location.pathname === '/' ? '/' : false}
            onChange={handleMobileNavigation}
            sx={{ height: 68, bgcolor: '#fff', '& .MuiBottomNavigationAction-root': { minWidth: 0, color: '#667085' }, '& .Mui-selected': { color: '#0877dc' }, '& .MuiBottomNavigationAction-label': { fontSize: '0.72rem', fontWeight: 600 } }}
          >
            <BottomNavigationAction label="Home" value="/" icon={<Home />} />
            <BottomNavigationAction label="Payments" value="/?section=payments" icon={<Payments />} />
            <BottomNavigationAction label="Visitors" value="/visitors" icon={<GroupAdd />} />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default Layout;
